import { useParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import breakpoints from '../../../config/Breakpoints'
import RouteContainer from '../../../common/styles/RouteContainer'
import RowSpacer from '../../../common/styles/RowSpacer'
import NoContentForFullScreen from '../../../common/NoContentForFullScreen'
import useBookAuthor from './useBookAuthor'
import useHtmlTitle from '../../../common/useHtmlTitle'
import booksitoutIcon from '../../../config/BooksitoutIcon'
import CardBodyContentContainer from '../../../common/styles/CardBodyContentContainer'
import LoadingBar from '../../../common/LoadingBar'
import { Card } from 'react-bootstrap'
import { getBookLanguageDisplayName } from '../BookType'
import type { BookLanguage } from '../BookType'
import BookIsbnCard, { BookIsbnCardGrid } from '../common/BookIsbnCard'
import useGridColumns from '../../../common/hooks/useGridColumns'

const NATIONALITY_LABEL: Record<string, string> = {
	US: '🇺🇸 미국',
	CHINA: '🇨🇳 중국',
	GERMANY: '🇩🇪 독일',
	JAPAN: '🇯🇵 일본',
	INDIA: '🇮🇳 인도',
	UK: '🇬🇧 영국',
	FRANCE: '🇫🇷 프랑스',
	ITALY: '🇮🇹 이탈리아',
	BRAZIL: '🇧🇷 브라질',
	CANADA: '🇨🇦 캐나다',
	RUSSIA: '🇷🇺 러시아',
	SOUTH_KOREA: '🇰🇷 대한민국',
	SPAIN: '🇪🇸 스페인',
	AUSTRALIA: '🇦🇺 호주',
	MEXICO: '🇲🇽 멕시코',
	INDONESIA: '🇮🇩 인도네시아',
	NETHERLANDS: '🇳🇱 네덜란드',
	SAUDI_ARABIA: '🇸🇦 사우디아라비아',
	TURKEY: '🇹🇷 튀르키예',
	TAIWAN: '🇹🇼 대만',
	ISRAEL: '🇮🇱 이스라엘',
	SWEDEN: '🇸🇪 스웨덴',
	SOUTH_AFRICA: '🇿🇦 남아프리카',
	PORTUGAL: '🇵🇹 포르투갈',
	COLOMBIA: '🇨🇴 콜롬비아',
	AUSTRIA: '🇦🇹 오스트리아',
	IRELAND: '🇮🇪 아일랜드',
	BELGIUM: '🇧🇪 벨기에',
	GREECE: '🇬🇷 그리스',
	IRAN: '🇮🇷 이란',
	NORWAY: '🇳🇴 노르웨이',
	NEW_ZEALAND: '🇳🇿 뉴질랜드',
	POLAND: '🇵🇱 폴란드',
	CZECH: '🇨🇿 체코',
	HUNGARY: '🇭🇺 헝가리',
	SWITZERLAND: '🇨🇭 스위스',
	CHILE: '🇨🇱 칠레',
	ARGENTINA: '🇦🇷 아르헨티나',
	PERU: '🇵🇪 페루',
	DENMARK: '🇩🇰 덴마크',
	FINLAND: '🇫🇮 핀란드',
	EGYPT: '🇪🇬 이집트',
	NIGERIA: '🇳🇬 나이지리아',
	UKRAINE: '🇺🇦 우크라이나',
	VIETNAM: '🇻🇳 베트남',
	PHILIPPINES: '🇵🇭 필리핀',
	SINGAPORE: '🇸🇬 싱가포르',
	ICELAND: '🇮🇸 아이슬란드',
	ROMANIA: '🇷🇴 루마니아',
}

const BookAuthorRoute = () => {
	const { urlName } = useParams()
	const [data, isLoading, isError] = useBookAuthor(urlName ?? null)
	const [gridRef, columns] = useGridColumns()

	useHtmlTitle(data?.author.name ?? '작가')

	if (isLoading) return (
		<RouteContainer>
			<RowSpacer size={40} />
			<SkeletonHeader>
				<SkeletonCircle />
				<RowSpacer size={15} />
				<SkeletonBar width={200} height={32} />
				<RowSpacer size={10} />
				<SkeletonBar width={150} height={18} />
				<RowSpacer size={8} />
				<SkeletonBar width={100} height={16} />
			</SkeletonHeader>
			<RowSpacer size={30} />
			<LoadingBar size={3} />
			<RowSpacer size={30} />
			<BookIsbnCardGrid ref={gridRef}>
				{Array.from({ length: columns || 7 }).map((_, i) => (
					<div key={i}>
						<Card className="h-100">
							<CardBodyContentContainer>
								<SkeletonBookCover />
								<RowSpacer size={10} />
								<LoadingBar size={3} />
								<RowSpacer size={5} />
								<LoadingBar size={2} />
							</CardBodyContentContainer>
						</Card>
					</div>
				))}
			</BookIsbnCardGrid>
			<RowSpacer size={40} />
		</RouteContainer>
	)
	if (isError || data == null) return <NoContentForFullScreen message={'작가 정보를 찾을 수 없어요'} />

	const { author, books } = data
	const isAdmin = localStorage.getItem('is-internal') === 'true'

	return (
		<RouteContainer>
			{isAdmin && author.urlName && (
				<AdminButton href={`/admin/author/${author.urlName}`}>
					<booksitoutIcon.settings size={20} />
				</AdminButton>
			)}

			<RowSpacer size={40} />

			<AuthorHeader>
				{author.imageUrl ? (
					<AuthorImage src={author.imageUrl} alt={author.name} />
				) : (
					<AuthorIconWrapper>
						<booksitoutIcon.user size={40} />
					</AuthorIconWrapper>
				)}
				<RowSpacer size={15} />
				<AuthorName>{author.name}</AuthorName>
				{author.originalName && <AuthorOriginalName>{author.originalName}</AuthorOriginalName>}
				{author.nationality && <AuthorNationality>{NATIONALITY_LABEL[author.nationality] ?? author.nationality}</AuthorNationality>}
				{author.description && (
					<>
						<RowSpacer size={10} />
						<AuthorDescription>{author.description}</AuthorDescription>
					</>
				)}
			</AuthorHeader>

			<RowSpacer size={30} />

			{(() => {
				const grouped = Object.entries(
					books.reduce((groups, book) => {
						const lang = book.language ?? 'UNKNOWN'
						if (!groups[lang]) groups[lang] = []
						groups[lang].push(book)
						return groups
					}, {} as Record<string, typeof books>)
				)
				const multiLang = grouped.length >= 2

				return multiLang ? (
					grouped.map(([language, groupBooks]) => (
						<div key={language}>
							<SectionHeader>
								<SectionTitle>도서 ({getBookLanguageDisplayName(language as BookLanguage)})</SectionTitle>
								<SectionCount>{groupBooks.length}</SectionCount>
							</SectionHeader>
							<RowSpacer size={10} />
							<BookIsbnCardGrid ref={gridRef}>
								{groupBooks.map(book => (
									<BookIsbnCard key={book.isbn13} book={book} />
								))}
							</BookIsbnCardGrid>
							<RowSpacer />
						</div>
					))
				) : (
					<>
						<SectionHeader>
							<SectionTitle>도서</SectionTitle>
							<SectionCount>{books.length}</SectionCount>
						</SectionHeader>
						<RowSpacer size={10} />
						<BookIsbnCardGrid ref={gridRef}>
							{books.map(book => (
								<BookIsbnCard key={book.isbn13} book={book} />
							))}
						</BookIsbnCardGrid>
					</>
				)
			})()}

			{author.imageUrl && author.imageCredit && (
				<>
					{/* 소개 ↔ 도서 간격(30 + SectionHeader padding-top 20)과 맞춤 */}
					<RowSpacer size={50} />
					<ImageCredit>사진: {author.imageCredit}</ImageCredit>
				</>
			)}

			<RowSpacer size={40} />
		</RouteContainer>
	)
}

const skeletonGlow = keyframes`
	0% { background-color: #e0e0e0; }
	40% { background-color: #d6d6d6; }
	60% { background-color: #c8c8c8; }
	100% { background-color: #e0e0e0; }
`

const SkeletonHeader = styled.div`
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-bottom: 20px;
`

const SkeletonBar = styled.div<{ width: number; height: number }>`
	width: ${props => props.width}px;
	height: ${props => props.height}px;
	background-color: #e0e0e0;
	border-radius: 4px;
	animation: ${skeletonGlow} 2s infinite;
`

const SkeletonCircle = styled.div`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	background-color: #e0e0e0;
	margin: 0 auto;
	animation: ${skeletonGlow} 2s infinite;
`

const SkeletonBookCover = styled.div`
	height: 180px;
	background-color: #e0e0e0;
	border-radius: 6px;
	animation: ${skeletonGlow} 2s infinite;
`

const AuthorHeader = styled.div`
	text-align: center;
`

const AuthorImage = styled.img`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	object-fit: cover;
`

const AuthorIconWrapper = styled.div`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	background-color: var(--card-card-bg-color);
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto;
	color: var(--sub-text-color);
`

const AuthorName = styled.h1`
	font-size: 2rem;
	font-weight: bold;
	color: var(--text-color);
`

const AuthorOriginalName = styled.h2`
	font-size: 1.2rem;
	color: var(--sub-text-color);
	font-style: italic;
`

const AuthorNationality = styled.p`
	font-size: 14px;
	color: var(--sub-text-color);
	margin: 0;
`

const AuthorDescription = styled.p`
	font-size: 15px;
	color: var(--text-color);
	line-height: 1.7;
	max-width: 700px;
	margin: 0 auto;
`

const ImageCredit = styled.p`
	font-size: 11px;
	color: var(--text-color);
	opacity: 0.45;
	text-align: center;
	margin: 0;
	word-break: break-word;
`

const SectionHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding-top: 20px;
	padding-bottom: 10px;
`

const SectionTitle = styled.h3`
	font-size: 1.2rem;
	font-weight: bold;
	color: var(--text-color);
	margin: 0;
`

const SectionCount = styled.span`
	font-size: 1rem;
	color: var(--sub-text-color);
`

const AdminButton = styled.a`
	position: fixed;
	z-index: 10;
	width: 50px;
	height: 50px;
	border-radius: 50%;
	background-color: #6c757d;
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	text-decoration: none;
	right: 20px;
	bottom: 90px;

	@media screen and (min-width: ${breakpoints.md}) {
		right: 30px;
		bottom: 100px;
	}

	@media screen and (min-width: ${breakpoints.xl}) {
		right: 60px;
		bottom: 130px;
	}

	&:hover {
		color: white;
		background-color: #5a6268;
	}
`

export default BookAuthorRoute
